import React, { useEffect, useRef, useState } from "react";
import { Button } from "@material-ui/core";
import { getTherapistAvailability } from "../../../../api/appointment";
import { alertError } from "../../../../utils/logger";

const TIME_ZONE = "Europe/Madrid";

function madridDateString(value) {
    return new Intl.DateTimeFormat("en-CA", { timeZone: TIME_ZONE }).format(new Date(value));
}

function addDays(dateStr, days) {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

function formatDayHeader(dateStr) {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Intl.DateTimeFormat("es-ES", {
        timeZone: "UTC",
        weekday: "short",
        day: "numeric",
        month: "numeric",
    }).format(new Date(Date.UTC(y, m - 1, d)));
}

function formatTime(iso) {
    return new Intl.DateTimeFormat("es-ES", {
        timeZone: TIME_ZONE,
        hour: "2-digit",
        minute: "2-digit",
    }).format(new Date(iso));
}

export function formatSlot(iso) {
    if (!iso) return "";
    return new Intl.DateTimeFormat("es-ES", {
        timeZone: TIME_ZONE,
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
    }).format(new Date(iso));
}

const cellStyle = {
    padding: 2,
    textAlign: "center",
};

const slotStyle = {
    width: "100%",
    padding: "4px 0",
    border: "1px solid #c8e6c9",
    borderRadius: 4,
    background: "#e8f5e9",
    color: "#2e7d32",
    fontSize: "0.8rem",
    cursor: "pointer",
};

const busyStyle = {
    ...slotStyle,
    border: "1px solid #eee",
    background: "#f3f3f3",
    color: "#bbb",
    cursor: "not-allowed",
};

const selectedStyle = {
    ...slotStyle,
    border: "1px solid #1b5e20",
    background: "#2e7d32",
    color: "#fff",
    fontWeight: 600,
};

/**
 * Cuadrícula semanal con las franjas libres del terapeuta (según su Google Calendar).
 * value / onChange: inicio de la cita en ISO.
 */
export default function AvailabilityGrid({
    clientId,
    therapistId,
    durationMinutes,
    excludeAppointmentId,
    refreshKey,
    value,
    onChange,
}) {
    const [weekStart, setWeekStart] = useState(value ? madridDateString(value) : "");
    const [availability, setAvailability] = useState(null);
    const [loading, setLoading] = useState(false);
    const requestRef = useRef(0);

    useEffect(() => {
        if (value) setWeekStart(madridDateString(value));
        // Solo al abrir la edición de una cita existente
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [excludeAppointmentId]);

    useEffect(() => {
        if (!clientId || !therapistId) {
            setAvailability(null);
            return undefined;
        }
        const requestId = ++requestRef.current;
        const timer = setTimeout(() => {
            setLoading(true);
            getTherapistAvailability({
                clientId,
                therapistId,
                weekStart: weekStart || undefined,
                durationMinutes: Number(durationMinutes) || 60,
                excludeAppointmentId: excludeAppointmentId || undefined,
            })
                .then((res) => {
                    if (requestId !== requestRef.current) return;
                    setAvailability(res.data);
                    if (!weekStart && res.data?.weekStart) setWeekStart(res.data.weekStart);
                })
                .catch((error) => {
                    if (requestId !== requestRef.current) return;
                    setAvailability(null);
                    alertError({
                        error,
                        customMessage: "No se ha podido cargar la disponibilidad del terapeuta.",
                    });
                })
                .finally(() => {
                    if (requestId === requestRef.current) setLoading(false);
                });
        }, 300);
        return () => clearTimeout(timer);
    }, [clientId, therapistId, weekStart, durationMinutes, excludeAppointmentId, refreshKey]);

    useEffect(() => {
        // Si la franja elegida deja de estar libre (otra duración, otro terapeuta), se deselecciona
        if (!value || !availability) return;
        const slot = availability.days
            .flatMap((day) => day.slots)
            .find((s) => s.start === value);
        if (slot && !slot.available) onChange("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availability]);

    if (!therapistId) {
        return <p style={{ color: "#888" }}>Selecciona un terapeuta para ver su disponibilidad.</p>;
    }

    const days = availability?.days || [];
    const rows = days[0]?.slots.length || 0;
    const currentWeek = availability?.weekStart || weekStart;
    const today = madridDateString(new Date());

    return (
        <div style={{ marginTop: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Button
                    size="small"
                    disabled={loading || !currentWeek || currentWeek <= today}
                    onClick={() => setWeekStart(addDays(currentWeek, -7))}
                >
                    ‹ Anterior
                </Button>
                <strong style={{ fontSize: "0.9rem" }}>
                    {currentWeek
                        ? `Semana del ${formatDayHeader(currentWeek)}`
                        : "Disponibilidad"}
                    {loading ? " · cargando..." : ""}
                </strong>
                <Button
                    size="small"
                    disabled={loading || !currentWeek}
                    onClick={() => setWeekStart(addDays(currentWeek, 7))}
                >
                    Siguiente ›
                </Button>
            </div>

            {availability?.calendarUnavailable && (
                <p style={{ color: "#c62828", fontSize: "0.85rem" }}>
                    No se ha podido leer el Google Calendar de este terapeuta: solo se tienen en
                    cuenta las citas de la intranet.
                </p>
            )}

            {availability?.noSchedule && (
                <p style={{ color: "#c62828", fontSize: "0.85rem" }}>
                    Este terapeuta no tiene ningún día disponible en su horario de citas.
                </p>
            )}

            {rows > 0 && (
                <div style={{ maxHeight: 320, overflowY: "auto", marginTop: 8 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                        <thead>
                            <tr>
                                {days.map((day) => (
                                    <th
                                        key={day.date}
                                        style={{
                                            ...cellStyle,
                                            fontSize: "0.8rem",
                                            textTransform: "capitalize",
                                            position: "sticky",
                                            top: 0,
                                            background: "#fff",
                                        }}
                                    >
                                        {formatDayHeader(day.date)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: rows }).map((_, rowIndex) => (
                                <tr key={rowIndex}>
                                    {days.map((day) => {
                                        const slot = day.slots[rowIndex];
                                        const selected = slot.start === value;
                                        return (
                                            <td key={slot.start} style={cellStyle}>
                                                <button
                                                    type="button"
                                                    disabled={!slot.available}
                                                    style={
                                                        selected
                                                            ? selectedStyle
                                                            : slot.available
                                                                ? slotStyle
                                                                : busyStyle
                                                    }
                                                    onClick={() => onChange(slot.start)}
                                                >
                                                    {formatTime(slot.start)}
                                                </button>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <p style={{ marginTop: 8, fontSize: "0.9rem" }}>
                {value ? (
                    <>
                        Cita seleccionada: <strong>{formatSlot(value)}</strong>
                    </>
                ) : (
                    <span style={{ color: "#888" }}>Elige una franja libre (en verde).</span>
                )}
            </p>
        </div>
    );
}
